import { useDroppable } from "@dnd-kit/core";
import AddIcon from "@mui/icons-material/Add";
import WarningIcon from "@mui/icons-material/Warning";
import { Box } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { DateTime } from "luxon";
import type React from "react";
import { type FC, type MouseEvent, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { InHouseTooltip } from "../../../../components/InHouseTooltip/InHouseTooltip";
import SystemButton from "../../../../components/UIToolkit/SystemButton/SystemButton";
import { useChosenDepartment } from "../../../../contexts/ChosenDepartment";
import { FEATURE_FLAGS } from "../../../../contexts/FeatureFlagsProvider";
import { useFeatureFlag } from "../../../../hooks/useOpenFeatureFlag";
import type { DepartmentLocation, Nurse, RecurringShift } from "../../../../utils/models";
import {
	addButtonLeadingIconGap,
	addButtonStyle,
	blockedCellSx,
	emptyGridShiftCellStyle,
	getDayExpectedAmount,
	gridShiftCellStyle,
	type ScheduleDayAtLocationData,
	warningIconStyle,
} from "../utils";
import { DraggableShift } from "./DraggableShift";

type DroppableCellProps = {
	day: ScheduleDayAtLocationData;
	cellDepartmentLocation: DepartmentLocation;
	isBlocked: boolean;
	colIndex: number;
	unfilledSlotsAmount: number;
	onShiftClick: (recurringShift: RecurringShift, event: MouseEvent<HTMLElement>, clickedDate: DateTime, clickedLocation: DepartmentLocation) => void;
	onShiftKeyDown: (recurringShift: RecurringShift, event: React.KeyboardEvent<HTMLElement>, clickedDate: DateTime, clickedLocation: DepartmentLocation) => void;
	handleOnAddClick: (clickedDate: DateTime, clickedLocation: DepartmentLocation) => void;
	isDraggingInContext: boolean;
	onCtrlPressedChange?: (isCtrlPressed: boolean) => void;
	isSynergyMS: boolean;
	isDraggingSection: boolean;
	getNurseById: (id: number) => Nurse | undefined;
};

export type DroppableCellOverData = {
	cellDay: ScheduleDayAtLocationData;
	cellDepartmentLocation: DepartmentLocation;
};

export const DroppableCell: FC<DroppableCellProps> = ({
	day,
	cellDepartmentLocation,
	isBlocked,
	colIndex,
	unfilledSlotsAmount,
	onShiftClick,
	onShiftKeyDown,
	handleOnAddClick,
	isDraggingInContext,
	onCtrlPressedChange,
	isSynergyMS,
	isDraggingSection,
	getNurseById,
}) => {
	const { t } = useTranslation();
	const { chosenDepartment } = useChosenDepartment();
	const isMasterScheduleEditBlocked = chosenDepartment.disable_edit_master_schedule;
	const overstaffingHighlightEnabled = useFeatureFlag(FEATURE_FLAGS.MasterScheduleOverstaffingHighlight);

	const droppableCellOverData: DroppableCellOverData = useMemo(
		() => ({
			cellDay: day,
			cellDepartmentLocation: cellDepartmentLocation,
		}),
		[day, cellDepartmentLocation],
	);

	const { setNodeRef, isOver } = useDroppable({
		id: `${day.dayDate.toISO()}|${cellDepartmentLocation.id}`,
		data: droppableCellOverData,
		disabled: isDraggingSection,
	});
	const expectedAmount = getDayExpectedAmount(cellDepartmentLocation, day.dayDate);
	const moreShiftsThanExpected = day.recurringShiftsInCell.length > expectedAmount;
	return (
		<Box
			ref={setNodeRef}
			data-testid={`master-schedule-droppable-${day.dayDate.toISODate()}-${cellDepartmentLocation.id ?? "no-id"}`}
			sx={(theme) => ({
				...gridShiftCellStyle,
				...(isBlocked ? blockedCellSx : {}),
				...(isOver && !isDraggingSection && !isBlocked
					? {
							// highlight the cell when something is dragged over it
							p: 0,
							backgroundColor: alpha(theme.palette.primary.main, 0.12),
							border: `1px dashed ${theme.palette.primary.main}`,
						}
					: {}),
				...(moreShiftsThanExpected && overstaffingHighlightEnabled && !isBlocked
					? {
							backgroundColor: "rgba(255, 0, 0, 0.2)",
						}
					: {}),
			})}
			data-col-index={colIndex}
			onMouseEnter={() => {
				if (!isDraggingSection) {
					//disable hover animations when dragging section
					const header = document.querySelector(`.ms2-col-header[data-col-index="${colIndex}"]`);
					if (header) header.classList.add("ms2-col-header-hovered");
				}
			}}
			onMouseLeave={() => {
				if (!isDraggingSection) {
					//disable hover animations when dragging section
					const header = document.querySelector(`.ms2-col-header[data-col-index="${colIndex}"]`);
					if (header) header.classList.remove("ms2-col-header-hovered");
				}
			}}
		>
			{day.recurringShiftsInCell.length > 0 &&
				day.recurringShiftsInCell.map((recurringShiftInCell, index) => {
					const relevantNurse = getNurseById(recurringShiftInCell.recurringShift.nurseId);
					return (
						<DraggableShift
							key={recurringShiftInCell.isOptimisticallyAdded ? index : recurringShiftInCell.recurringShift.id}
							onShiftClick={onShiftClick}
							onShiftKeyDown={onShiftKeyDown}
							recurringShift={recurringShiftInCell.recurringShift}
							clickedLocation={cellDepartmentLocation}
							recurringShiftId={recurringShiftInCell.recurringShift.id}
							isFutureShift={recurringShiftInCell.isFutureShift}
							multipleShifts={day.recurringShiftsInCell.length > 1 || unfilledSlotsAmount > 0}
							nurseId={recurringShiftInCell.recurringShift.nurseId}
							recurringShiftStartDate={recurringShiftInCell.recurringShift.startDate}
							relevantNurse={relevantNurse}
							timeOffDuringShift={recurringShiftInCell.longPtoForShift}
							day={day}
							isDraggingInContext={isDraggingInContext}
							onCtrlPressedChange={onCtrlPressedChange}
							optimisticallyAddedShift={recurringShiftInCell.isOptimisticallyAdded}
							isSynergyMS={isSynergyMS}
							isCredentialed={recurringShiftInCell.isCredentialed}
						/>
					);
				})}
			{Array.from({ length: unfilledSlotsAmount }, (_, index) => (
				// biome-ignore lint/suspicious/noArrayIndexKey: We don't have a unique key for these empty cells, so we use index
				<Box key={day.dayNumber + index} sx={emptyGridShiftCellStyle}>
					<SystemButton
						buttonVariant={"borderless"}
						sx={addButtonStyle}
						leadingIconGap={addButtonLeadingIconGap}
						onClick={(_) => handleOnAddClick(day.dayDate, cellDepartmentLocation)}
						leadingIcon={<AddIcon fontSize={"small"} />}
						data-testid="master-schedule-add-shift"
						// Not an anchor — the grid is. This marks the control the tour's Next presses to open
						// the recurring panel, so the spotlight can stay on the grid (where every cell stays
						// clickable) while Next still does the opening. See `revealTriggerWithin`.
						data-walkthrough-reveal="true"
						disabled={isMasterScheduleEditBlocked}
						tooltip={isMasterScheduleEditBlocked ? t("schedule.template.edit.blocked") : undefined}
					/>
				</Box>
			))}
			{moreShiftsThanExpected && (
				<InHouseTooltip title={t("schedule.template.more.shifts.than.configured", { actual: day.recurringShiftsInCell.length, expected: expectedAmount })}>
					<WarningIcon style={warningIconStyle} />
				</InHouseTooltip>
			)}
		</Box>
	);
};
